import { useState, useEffect } from "react";
import axios, { API } from "../../api";

/**
 * Refer-a-friend modal. Shows the user's personal referral code, copy/share buttons,
 * and a list of friends who already joined via the code.
 * Restricted to paying users on the backend (403 otherwise).
 */
export function ReferralModal({ onClose, onUpgrade }) {
  const [loading, setLoading] = useState(true);
  const [code, setCode] = useState(null);
  const [shareText, setShareText] = useState("");
  const [shareUrl, setShareUrl] = useState("");
  const [rewardForFriend, setRewardForFriend] = useState("");
  const [rewardForYou, setRewardForYou] = useState("");
  const [friendsJoined, setFriendsJoined] = useState(0);
  const [referrals, setReferrals] = useState([]);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const [copiedTarget, setCopiedTarget] = useState(null);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/me/referral-code`);
      setCode(res.data.code);
      setShareText(res.data.share_text);
      setShareUrl(res.data.share_url || "");
      setRewardForFriend(res.data.reward_for_friend || res.data.reward_for_both || "");
      setRewardForYou(res.data.reward_for_you || res.data.reward_for_both || "");
      setFriendsJoined(res.data.friends_joined || 0);
      const refRes = await axios.get(`${API}/me/referrals`);
      setReferrals(refRes.data.referrals || []);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Couldn't load referral info";
      setError(detail);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleCopyCode = () => {
    if (!code) return;
    navigator.clipboard?.writeText(code).then(() => {
      setCopied(true);
      setCopiedTarget("code");
      setTimeout(() => setCopied(false), 1800);
    });
  };

  const handleCopyLink = () => {
    if (!shareUrl) return;
    navigator.clipboard?.writeText(shareUrl).then(() => {
      setCopied(true);
      setCopiedTarget("link");
      setTimeout(() => setCopied(false), 1800);
    });
  };

  const handleShare = async () => {
    if (!shareText) return;
    // Use the Web Share API when available (mobile), fall back to clipboard
    if (navigator.share) {
      try {
        await navigator.share({ text: shareText, title: "Try Aura with me" });
        return;
      } catch (e) { /* user cancelled or unsupported */ }
    }
    navigator.clipboard?.writeText(shareText).then(() => {
      setCopied(true);
      setCopiedTarget("share");
      setTimeout(() => setCopied(false), 1800);
    });
  };

  const isUpgradeWall = error.toLowerCase().includes("paid plan") || error.toLowerCase().includes("subscribe");

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="referral-modal" onClick={(e) => e.stopPropagation()} data-testid="referral-modal">
        <div className="journal-header">
          <h2>👥 Refer a friend</h2>
          <button className="journal-close" onClick={onClose} data-testid="referral-close">×</button>
        </div>

        {loading ? (
          <p style={{ opacity: 0.7, textAlign: "center", padding: "1rem" }}>Loading...</p>
        ) : isUpgradeWall ? (
          <div className="referral-locked" data-testid="referral-locked">
            <div className="referral-emoji">🔒</div>
            <h3>Unlocked with any paid plan</h3>
            <p>Start a Base or Premium plan and you'll get your own referral code. Every friend who joins with it gives you both a free month of Premium.</p>
            {onUpgrade && (
              <button className="init-btn" onClick={onUpgrade} data-testid="referral-upgrade-btn">
                See plans
              </button>
            )}
          </div>
        ) : error ? (
          <div className="error-message" data-testid="referral-error">{error}</div>
        ) : (
          <>
            <p className="referral-tagline">
              Friends who use your code get <strong>1 free month of Premium + 25% off lifetime Base</strong>. You get <strong>1 free month of Premium</strong> for every friend who joins (stacks!).
            </p>

            <div className="referral-code-box" data-testid="referral-code-box">
              <code className="referral-code-text" data-testid="referral-code-text">{code}</code>
              <button
                className="referral-copy-btn"
                onClick={handleCopyCode}
                data-testid="referral-copy-btn"
              >
                {copied && copiedTarget === "code" ? "✓ Copied" : "Copy"}
              </button>
            </div>

            {shareUrl && (
              <div className="referral-link-box" data-testid="referral-link-box">
                <span className="referral-link-text">{shareUrl}</span>
                <button
                  className="referral-copy-btn small"
                  onClick={handleCopyLink}
                  data-testid="referral-copy-link-btn"
                >
                  {copied && copiedTarget === "link" ? "✓" : "Copy link"}
                </button>
              </div>
            )}

            <button
              className="init-btn"
              onClick={handleShare}
              data-testid="referral-share-btn"
              style={{ marginBottom: "0.75rem" }}
            >
              📤 Share with a friend
            </button>

            <div className="referral-stat" data-testid="referral-stat">
              <span className="referral-stat-num">{friendsJoined}</span>
              <span className="referral-stat-label">{friendsJoined === 1 ? "friend has joined" : "friends have joined"}</span>
            </div>

            {referrals.length > 0 && (
              <div className="referral-list" data-testid="referral-list">
                <p className="referral-list-title">Who joined</p>
                {referrals.map((r, i) => (
                  <div key={i} className="referral-list-row">
                    <span>{r.user_name || "A friend"}</span>
                    <span style={{ opacity: 0.55 }}>{(r.redeemed_at || "").slice(0, 10)}</span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
